import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPanelAdminErrorMessage,
  PanelListParams,
  panelsService,
  SavePanelInput,
} from "../../services/admin/panels.service";

const PANELS_QUERY_KEY = ["admin", "panels"] as const;

export function usePanels(params: PanelListParams) {
  return useQuery({
    queryKey: [...PANELS_QUERY_KEY, params],
    queryFn: () => panelsService.list(params),
    placeholderData: (previousData) => previousData,
  });
}

export function usePanel(id?: string) {
  return useQuery({
    queryKey: [...PANELS_QUERY_KEY, id],
    queryFn: () => panelsService.getById(id as string),
    enabled: Boolean(id),
  });
}

function showPanelStatusError(error: unknown) {
  notifications.show({
    title: "Panel status was not changed",
    message: getPanelAdminErrorMessage(error),
    color: "red",
    autoClose: 5000,
  });
}

export function useCreatePanel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SavePanelInput) => panelsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PANELS_QUERY_KEY });
      notifications.show({
        title: "Panel created",
        message: "The panel is ready for accessioning.",
        color: "green",
        autoClose: 4000,
      });
    },
  });
}

export function useCreatePanelVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SavePanelInput }) =>
      panelsService.createVersion(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PANELS_QUERY_KEY });
      notifications.show({
        title: "Panel version created",
        message: "New orders will use the updated panel version.",
        color: "green",
        autoClose: 4000,
      });
    },
  });
}

export function useUpdatePanelStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      panelsService.updateStatus(id, isActive),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: PANELS_QUERY_KEY });
      notifications.show({
        title: variables.isActive ? "Panel activated" : "Panel deactivated",
        message: variables.isActive
          ? "The panel is available for new orders."
          : "The panel is hidden from new orders.",
        color: variables.isActive ? "green" : "orange",
        autoClose: 4000,
      });
    },
    onError: showPanelStatusError,
  });
}
